/* ===========================================================================
   Dignity filter sheet — the behaviour that turns Dawn's mobile facets drawer
   into one sheet.

   Nothing in snippets/facets.liquid is forked: Dawn's own markup is opened
   out and re-dressed here, so a theme update needs no merge and a failure in
   this file leaves the customer with Dawn's stock drawer, which works.

   What it does, on phones only:

     1. Opens every filter group and puts them in selling order — Size,
        Colour, Pattern, Fabric weave. Fit comes first because it is the only
        filter that can stop a sale.
     2. Hides a filter that cannot do anything. Which filters exist is a
        STORE setting (the Search & Discovery app) and is shared with search,
        so Availability and Price stay switched on there and are hidden here
        instead: Availability on the collection pages, where the answer is
        always "nearly all of them", and Price on any collection whose shirts
        all carry one price. Never to the point of an empty sheet.
     3. Cuts the sort list to the choices that can reorder these shirts —
        Featured, Best selling, New arrivals — renamed into the brand's words.
     4. Writes the count onto the footer button ("Show 34 shirts") so the
        confirmation and the button are the same thing, and adds the sheet's
        own close and headings.
     5. "My size". The bundle offer asks a man for two to four shirts; the
        fastest way to lose that basket is a size dead-end on the third. The
        size he picks anywhere (the product sheet's drawer, the photo drawer,
        or a single size chosen here) is remembered in this browser and
        offered back as one tap. It never filters on its own.
     6. Puts the number of applied filters on the floating dock pill.

   No filter is ever applied from here directly: every change ticks the real
   input and lets facets.js hear the input event, exactly as a tap would.
   =========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'dignity:my-size';
  var SORT_KEEP = ['manual', 'best-selling', 'created-descending'];
  var SORT_PRICE = ['price-ascending', 'price-descending'];
  var SORT_NAMES = {
    manual: 'Featured',
    'best-selling': 'Best selling',
    'created-descending': 'New arrivals',
    'price-ascending': 'Price, low to high',
    'price-descending': 'Price, high to low'
  };
  /* Visual order of the groups; anything unrecognised falls in after them. */
  var ORDER = ['size', 'colour', 'pattern', 'weave', 'other', 'price', 'availability', 'sort'];

  /* Storage throws in a private window and comes back empty after site data is
     cleared, so every call is guarded; an empty answer just means "ask him". */
  function readSize() {
    try {
      return window.localStorage.getItem(STORE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function writeSize(value) {
    try {
      if (value) window.localStorage.setItem(STORE_KEY, value);
    } catch (error) {
      /* a convenience, never a dependency */
    }
  }

  window.DignityMySize = { read: readSize, write: writeSize, key: STORE_KEY };

  function form() {
    return document.getElementById('FacetFiltersFormMobile');
  }

  function list(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function valueInputs(scope) {
    return list(scope, 'input[type="checkbox"]').filter(function (input) {
      return input.name && input.name !== 'sort_by';
    });
  }

  /* A Dignity collection page, as opposed to search results, which share this
     sheet but not its rules about what is worth showing. */
  function onCollectionPage() {
    return Boolean(document.querySelector('.dignity-collection-page'));
  }

  /* ------------------------------------------------- what each group is */
  function groupKind(group) {
    if (/SortBy/i.test(group.id || '')) return 'sort';
    if (group.querySelector('.facets__price, price-range')) return 'price';

    var names = valueInputs(group)
      .map(function (input) {
        return input.name.toLowerCase();
      })
      .join(' ');

    if (names.indexOf('availability') !== -1) return 'availability';
    if (names.indexOf('option.size') !== -1 || names.indexOf('.size') !== -1) return 'size';
    if (names.indexOf('color') !== -1 || names.indexOf('colour') !== -1) return 'colour';
    if (names.indexOf('filter_pattern') !== -1) return 'pattern';
    if (names.indexOf('filter_weave') !== -1) return 'weave';
    return 'other';
  }

  function sizeGroup() {
    var scope = form();
    if (!scope) return null;
    return (
      list(scope, '.mobile-facets__details').filter(function (group) {
        return groupKind(group) === 'size';
      })[0] || null
    );
  }

  /* Price sorts, and the Price group itself, earn their place only when the
     collection really holds more than one price. The grid already prints every
     shirt as JSON for the photo drawer, unfiltered, so that is the honest
     place to ask — a filtered view would answer for the wrong set. */
  var priceSortsAllowed = null;

  function pricesDiffer() {
    if (priceSortsAllowed !== null) return priceSortsAllowed;
    priceSortsAllowed = true;
    var payload = document.querySelector('[data-dcc-products]');
    if (!payload) return priceSortsAllowed;
    try {
      var seen = {};
      var count = 0;
      (JSON.parse(payload.textContent) || []).forEach(function (item) {
        if (!item || !item.price || seen[item.price]) return;
        seen[item.price] = true;
        count += 1;
      });
      if (count > 0) priceSortsAllowed = count > 1;
    } catch (error) {
      /* unreadable payload: leave it alone rather than hide something useful */
    }
    return priceSortsAllowed;
  }

  /* ------------------------------------- 1 & 2. open, order, hide the dead */
  function arrangeGroups() {
    var scope = form();
    if (!scope) return;

    var collection = onCollectionPage();
    var visible = [];

    var groups = list(scope, '.mobile-facets__details').map(function (group) {
      if (group.tagName === 'DETAILS' && !group.open) group.open = true;

      var kind = groupKind(group);
      if (group.dataset.dfsKind !== kind) {
        ORDER.forEach(function (name) {
          group.classList.remove('dfs-g-' + name);
        });
        group.classList.add('dfs-g-' + kind);
        group.dataset.dfsKind = kind;
      }
      return { group: group, kind: kind };
    });

    /* Availability on a collection page only ever offers to show him what he
       cannot buy; Price cannot narrow a collection that has one price. Both
       stay switched on in the app, for search and for later. */
    var dead = {};
    if (collection) {
      dead.availability = true;
      if (!pricesDiffer()) dead.price = true;
    }

    /* But a sheet with no filters at all is worse than one carrying a weak
       filter, so a dead group only stands down while a live one remains. The
       day Size, Colour, Pattern and Weave are published in the Search &
       Discovery app the weak ones disappear on their own; until then the
       customer still has something to press. */
    var live = groups.filter(function (entry) {
      return entry.kind !== 'sort' && !dead[entry.kind];
    }).length;
    if (!live) dead = {};

    groups.forEach(function (entry) {
      var hide = Boolean(dead[entry.kind]);
      entry.group.hidden = hide;
      if (!hide) visible.push({ group: entry.group, rank: ORDER.indexOf(entry.kind) });
    });

    /* The rule above the first group would double up with the header's own,
       and source order is no longer visual order, so it is marked here. */
    visible.sort(function (a, b) {
      return a.rank - b.rank;
    });
    visible.forEach(function (entry, index) {
      entry.group.classList.toggle('dfs-first', index === 0);
    });
  }

  /* --------------------------------------------------- 3. cut the sort list */
  function trimSorts() {
    var keep = SORT_KEEP.slice();
    if (pricesDiffer()) keep = keep.concat(SORT_PRICE);

    list(document, 'select[name="sort_by"]').forEach(function (select) {
      if (select.dataset.dfsTrimmed === '1') return;
      select.dataset.dfsTrimmed = '1';

      list(select, 'option').forEach(function (option) {
        /* Whatever he is sorted by now always stays, so the list can never
           drop the choice he is looking at. */
        if (option.value !== select.value && keep.indexOf(option.value) === -1) {
          option.remove();
          return;
        }
        if (SORT_NAMES[option.value]) option.textContent = SORT_NAMES[option.value];
      });
    });
  }

  /* --------------------------------------- 4. headings, button and close */
  function paintChrome() {
    var scope = form();
    if (!scope) return;

    /* The wording is ours, not the theme's translation file: that file is
       regenerated by the admin language editor and would take it with it. */
    list(scope, '.mobile-facets__heading').forEach(function (heading) {
      if (heading.textContent.trim() !== 'Filter & Sort') heading.textContent = 'Filter & Sort';
    });

    var sortLabel = scope.querySelector('.mobile-facets__sort label');
    if (sortLabel && sortLabel.textContent.trim() !== 'Sort by') sortLabel.textContent = 'Sort by';

    var count = scope.querySelector('.mobile-facets__count');
    var button = scope.querySelector('.mobile-facets__main > .mobile-facets__footer .button');
    if (count && button) {
      var figure = ((count.textContent || '').match(/\d[\d,]*/) || [''])[0];
      button.textContent = figure
        ? 'Show ' + figure + ' shirt' + (figure === '1' ? '' : 's')
        : 'Show results';
    }

    var header = scope.querySelector('.mobile-facets__header-inner');
    if (header && !header.querySelector('.dfs__close')) {
      /* A span, not a button: menu-drawer binds every button inside itself to
         close, and the handler below is the close this one needs. */
      var close = document.createElement('span');
      close.className = 'dfs__close';
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      close.setAttribute('aria-label', 'Close');
      close.textContent = '×';
      var shut = function () {
        var wrapper = scope.closest('.mobile-facets__wrapper');
        if (wrapper) wrapper.querySelector('summary').click();
      };
      close.addEventListener('click', shut);
      close.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          shut();
        }
      });
      header.appendChild(close);
    }
  }

  /* ------------------------------------------------------------ 5. my size */
  function paintMySize() {
    var group = sizeGroup();
    if (!group) return;

    var row = group.querySelector('.mobile-facets__summary > div');
    if (!row) return;

    var size = readSize();
    var target = valueInputs(group).filter(function (input) {
      return !input.disabled && input.value === size;
    })[0];

    var chip = row.querySelector('.dfs-mysize');
    if (!chip) {
      chip = document.createElement('span');
      chip.className = 'dfs-mysize';
      chip.setAttribute('role', 'button');
      chip.setAttribute('tabindex', '0');
      var press = function () {
        var current = sizeGroup();
        if (!current) return;
        var input = valueInputs(current).filter(function (candidate) {
          return candidate.value === readSize();
        })[0];
        if (!input || input.disabled) return;
        input.checked = !input.checked;
        /* The same events a real tap raises, so facets.js treats it the same. */
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      chip.addEventListener('click', press);
      chip.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          press();
        }
      });
      row.appendChild(chip);
    }

    if (!target) {
      chip.hidden = true;
      return;
    }

    chip.hidden = false;
    chip.innerHTML = 'My size <b></b>';
    chip.querySelector('b').textContent = size;
    chip.classList.toggle('is-on', target.checked);
  }

  /* A single chosen size is the clearest statement of what he wears; two or
     more is him browsing, and is not remembered. */
  function rememberSingleSize() {
    var group = sizeGroup();
    if (!group) return;
    var checked = valueInputs(group).filter(function (input) {
      return input.checked;
    });
    if (checked.length === 1) writeSize(checked[0].value);
  }

  /* --------------------------------------------------------- 6. dock badge */
  function paintBadge() {
    var pill = document.querySelector('[data-dme-filter]');
    if (!pill) return;
    var scope = form();

    var count = 0;
    if (scope) {
      count = valueInputs(scope).filter(function (input) {
        return input.checked;
      }).length;

      var priced = list(scope, '.facets__price input').some(function (input) {
        return input.value !== '';
      });
      if (priced) count += 1;
    }

    var badge = pill.querySelector('.dfs-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'dfs-badge';
      pill.appendChild(badge);
    }
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }

  function refresh() {
    arrangeGroups();
    trimSorts();
    paintChrome();
    paintMySize();
    paintBadge();
  }

  var queued = false;
  function queueRefresh() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      refresh();
    });
  }

  function start() {
    var scope = form();
    if (!scope) {
      trimSorts();
      return;
    }

    scope.addEventListener('change', function (event) {
      if (event.target && event.target.name && event.target.name !== 'sort_by') rememberSingleSize();
      queueRefresh();
    });

    /* facets.js rebuilds the groups from the server on every applied filter;
       the observer is what carries all six jobs across that rebuild. */
    new MutationObserver(queueRefresh).observe(scope, { childList: true, subtree: true });

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
